import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { saveAs } from 'file-saver';

import { PageableTableComponent } from '../../shared/controls/pageable-table/pageable-table.component';

import { DialogService } from '../../shared/controls/message-dialog/message-dialog.service';
import { ToastService } from '../../shared/toast/toast.service';
import { InitializerService } from '../../shared/services/initializer-service';
import { ErrorService } from '../../shared/services/error-service';
import { AuthService } from '../../shared/services/auth.service';
import { LoadingService } from '../../shared/services/loading.service';
import { LoadingHelper } from '../../shared/services/loading-helper';

import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { AreaConstants } from '../../shared/constants/area.constants';
import { PermissionConstants } from '../../shared/constants/permission.constants';

import { UserService, DownloadService, FilteredUserItem, PagingData } from '../../../service/proxy';
import { count } from 'rxjs';

@Component({
    templateUrl: './user.component.html',
    styleUrls: ['./user.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        PageableTableComponent,
        HasPermissionDirective,
    ],

    providers: [
        UserService,
        DownloadService,
        AuthService,
    ]
})
export class UserComponent implements OnInit {
    public intialized = false;

    public showEdit: boolean = false;
    public showDelete: boolean = false;

    public search: string = '';
    public paging: PagingData = new PagingData();
    public users: FilteredUserItem[] = [];

    constructor(
        public authService: AuthService,
        private router: Router,
        private userService: UserService,
        private downloadService: DownloadService,
        private dialogService: DialogService,
        private toastService: ToastService,
        private errorService: ErrorService,
        private initializerService: InitializerService,
        private loadingService: LoadingService)
        {
            this.paging = this.initializerService.createPagingData();
        }

    ngOnInit(): void {
        this.initializePermissions();
    }

    initializePermissions(): void {
        this.showEdit = this.authService
            .hasPermission(AreaConstants.User, PermissionConstants.Edit);

        this.showDelete = this.authService
            .hasPermission(AreaConstants.User, PermissionConstants.Delete);
    }

    refreshGridData(): void {
        this.paging = this.initializerService.createPagingData();
        this.getUsers(this.paging);
    }

    getUsers(pagingData: PagingData = new PagingData): void {
        const request$ = this.userService.getFilteredUsers(
            this.search, 
            pagingData.pageNo, 
            pagingData.pageSize, 
            pagingData.sortDirection, 
            pagingData.sortColumn
        );
        
        LoadingHelper.withLoading(request$, this.loadingService)
            .subscribe({
                next: (data) => {
                    this.paging = data.pagingData;
                    this.users = data.results || [];
                },
                error: (error) => {
                    if (error.status === 204) {
                        this.users = [];
                        this.paging = this.initializerService.createPagingData();
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                }
            });
    }

    getColumnMap() {
        return {
            username: 'User Name',
            firstname: 'First Name',
            lastname: 'Last Name',
            employeeNumber: 'Employee Number',
            userRoles: 'User Roles',
            tenants: 'Tenants',
            isActive: 'Active',
        };
    }

    toggleUserStatus(event: { item: any; newValue: boolean }): void {

        const apiCall = event.newValue
            ? this.userService.activateUser(event.item.id)
            : this.userService.deactivateUser(event.item.id);

        LoadingHelper.withLoading(apiCall, this.loadingService)
            .subscribe({
                next: () => {
                    event.item.isActive = event.newValue;

                    const actionText = event.newValue
                        ? 'activated'
                        : 'deactivated';

                    this.toastService.show(`User ${event.item.username} ${actionText} successfully`, 'Success');
                },
                error: (error) => {
                    this.errorService.handleError(error);
                }
            });
    }

    upsertUser(id: number): void {
        this.router.navigate([`/user/${id}`]);
    }

    deleteUser(id: number): void {
        this.dialogService.show('Delete', 'Are you sure you want to delete this record?', 'Warning')
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    LoadingHelper.withLoading(
                        this.userService.deleteUser(id),
                        this.loadingService
                    ).subscribe({
                        next: () => {
                            this.getUsers(this.paging);
                            this.toastService.show('User deleted successfully!', 'success');
                        },
                        error: (err) =>{
                            this.errorService.handleError(err);
                        },
                    });
                }
            }
        );
    }

    downloadUsers(pagingData: PagingData): void {
        const download$ = this.downloadService.downloadUsers(
            this.search, 
            pagingData.pageNo, 
            pagingData.pageSize, 
            pagingData.sortDirection, 
            pagingData.sortColumn
        );
        
        LoadingHelper.withLoading(download$, this.loadingService)
            .subscribe({
                next: (response) => {
                    if (response.status === 200) {
                        saveAs(response.data, 'Users.xlsx');
                    }
                    else {
                        alert('Download failed');
                    }
                },
                error: (error) => {
                    alert('Download failed');
                }
            });
    }
}
